import { z } from "zod";

import { appLocaleSchema, roleSchema } from "../auth/auth";
import { appViewSchema } from "../auth/views";
import { listQuerySchema } from "../common/list-query";
import { paginatedSchema } from "../common/pagination";

export const USER_STATUSES = ["ACTIVE", "INVITED", "DISABLED"] as const;
export const userStatusSchema = z.enum(USER_STATUSES);
export type UserStatus = z.infer<typeof userStatusSchema>;

/**
 * Roles this API can assign.
 *
 * SUPER_ADMIN is missing by design, not by omission: it lives on the user
 * record rather than on a membership (ADR-016), so no organization admin can
 * grant it through user management. Keeping it out of the schema makes that a
 * property of the contract instead of a check someone has to remember.
 */
export const assignableRoleSchema = z.enum(["USER", "ADMIN"]);
export type AssignableRole = z.infer<typeof assignableRoleSchema>;

export const userSchema = z.object({
  id: z.uuid(),
  email: z.email(),
  name: z.string(),
  /** Effective role, so SUPER_ADMIN can appear here even though it is not assignable. */
  role: roleSchema,
  status: userStatusSchema,
  organizationId: z.uuid().nullable(),
  organizationName: z.string().nullable(),
  /** Modules granted on this membership (ADR-088). Empty for a platform admin with no membership. */
  views: z.array(appViewSchema),
  lastLoginAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
});

export type User = z.infer<typeof userSchema>;

/**
 * Invitation, not registration: no password crosses this boundary. The user
 * is created INVITED and cannot authenticate until they set one via the
 * magic-link email (ADR-085). Login must reject a passwordless account
 * exactly like a wrong password.
 */
export const createUserRequestSchema = z.object({
  email: z.email().trim().toLowerCase().max(254),
  name: z.string().trim().min(2).max(120),
  role: assignableRoleSchema.default("USER"),
  /**
   * Modules to grant. Omitted → full ceiling for `role`. Must stay within
   * that ceiling (ADR-088).
   */
  views: z.array(appViewSchema).optional(),
  /**
   * Only a super admin may set this, and only they need it: for anyone else
   * the tenant comes from the session and this field is rejected.
   */
  organizationId: z.uuid().optional(),
  /** Drives invite email copy and User.preferredLocale (ADR-085). */
  locale: appLocaleSchema.optional(),
});

export type CreateUserRequest = z.infer<typeof createUserRequestSchema>;

/**
 * Email is not updatable here. Changing the identifier someone signs in with
 * needs a verification flow on the new address, and doing it without one turns
 * account management into account takeover.
 */
export const updateUserRequestSchema = z
  .object({
    name: z.string().trim().min(2).max(120),
    role: assignableRoleSchema,
    status: z.enum(["ACTIVE", "DISABLED"]),
    views: z.array(appViewSchema),
  })
  .partial()
  .refine((value) => Object.keys(value).length > 0, {
    message: "Provide at least one field to update.",
  });

export type UpdateUserRequest = z.infer<typeof updateUserRequestSchema>;

export const listUsersQuerySchema = listQuerySchema.extend({
  status: userStatusSchema.optional(),
  role: assignableRoleSchema.optional(),
});

export type ListUsersQuery = z.infer<typeof listUsersQuerySchema>;

export const userListSchema = paginatedSchema(userSchema);
export type UserList = z.infer<typeof userListSchema>;
